import { Injectable, inject } from '@angular/core';
import * as turf from '@turf/turf';
import { Feature } from '@turf/turf';
import * as _ from 'lodash';
import * as mapboxgl from 'mapbox-gl';
import { MapStateService } from '../../map/map-state.service';
import { MapboxService } from '../../map/mapbox-map/mapbox-map.service';
import { Layers } from '../../map/mapbox-map/models';
import { ActivityPoint } from '../../shared/models';
import { APP_CONTEXT } from '../models/app-state';
import { UserMapConfig } from '../models/user-map-config';
import { HistoryStore, LineProgressStore, StopStore, VehicleStore } from '../store';
import { AppStateService } from './app-state.service';
import { BlockActivityInfo, PassedPosition, VehicleHistory } from '../../shared/models/vehicle-history';
import { Stop } from '../../shared/models/stop';

@Injectable({
    providedIn: 'root'
})
export class MapService {
    private _appStateService = inject(AppStateService);
    private _mapStateService = inject(MapStateService);
    private _vehicleStore = inject(VehicleStore);
    private _mapboxService = inject(MapboxService);
    private _historyStore = inject(HistoryStore);
    private _stopStore = inject(StopStore);
    private _lineProgressStore = inject(LineProgressStore);

    private _isHistoryModeActive = false;
    private _historyVehicleInventoryNumber: string;
    private _lineFeatures: Feature[] = [];


    constructor() {
        // listens to all vehicle data changes (triggered from signalr-hub every second)
        this._vehicleStore.processedFeatures$.subscribe((features: Feature[]) => {
            // deactives the redraw if history mode is active
            if (!this._isHistoryModeActive) {
                this._mapboxService.triggerRedrawVehicleLayer(features);
            }
        });

        // listens to user click in the map to place the history highlighted item closest to that point in the history path line
        this._mapStateService.getMapPropertyState('clickedCoordinate')
            .subscribe((clickedCoordinate: {
                lng: number; lat: number;
            }) => {
                if (this._isHistoryModeActive && this._historyStore.hasPositions) {
                    this._historyStore.highlightByCoordinate(clickedCoordinate.lat, clickedCoordinate.lng);
                }
            });

        // listens to user settings for hiding and showing some map layers
        this._appStateService.getPropertyState('mapConfig')
            .subscribe((mapConfig: UserMapConfig) => {
                this._mapboxService.setVisibilityOnLayer('traffic', this._appStateService.isTrafficLayerVisible);
                this._mapboxService.setVisibilityOnLayer(Layers.RouteTileset, mapConfig.showRouteNetwork);
                this._mapboxService.setVisibilityOnLayer(Layers.StopsTileset, mapConfig.showStopsNetwork);
                this._mapboxService.setCityLabelsVisibility(this._appStateService.isCityLabelsVisible);
            });

        // listens to history mode activation and deactivation
        this._appStateService.getPropertyState('selectedContext')
            .subscribe((selectedContext: APP_CONTEXT) => {
                if (selectedContext === APP_CONTEXT.HISTORY) {
                    this.activateHistoryMode(true);
                    this._mapboxService.triggerRedrawVehicleLayer([]);
                    this._mapboxService.renderData(Layers.Vehicle, [], 'HistoryComponent');
                } else {
                    this.activateHistoryMode(false);
                }
            });

        // listens to history path line changes
        this._historyStore.getPropertyState('vehicleHistory')
            .subscribe((vehicleHistory: VehicleHistory) => {
                if (vehicleHistory) {
                    const passedPositions = vehicleHistory.passedPositions;
                    this._historyVehicleInventoryNumber = vehicleHistory.inventoryNumber;
                    this._lineFeatures = this._getLineFeaturesV2(passedPositions);
                    const pointFeatures = this._getPointFeatures(passedPositions);

                    this._mapboxService.renderData(Layers.Lines, this._lineFeatures, 'HistoryComponent');
                    this._mapboxService.renderData(Layers.TimingPoint, pointFeatures, 'HistoryComponent');

                    const bounds = this._getBounds(passedPositions);
                    this._mapboxService.fitBounds(bounds, 40);

                } else {
                    // clear!
                    this._lineFeatures = [];
                    this._historyVehicleInventoryNumber = undefined;
                    this._mapboxService.renderData(Layers.Lines, [], 'HistoryComponent');
                    this._mapboxService.renderData(Layers.Vehicle, [], 'HistoryComponent');
                    this._mapboxService.renderData(Layers.TimingPoint, [], 'HistoryComponent');
                    this._mapboxService.renderData(Layers.Stops, [], 'HistoryComponent');
                }
            });

        this._historyStore.getPropertyState('selectedActivity')
            .subscribe((activity: BlockActivityInfo) => {
                // Update or remove shape
                const lineFeatures = _.clone(this._lineFeatures);
                if (activity?.shapeGeometry) {
                    const plannedLine = this._getPlannedLineFeature(activity);
                    _.each(plannedLine, l => {
                        lineFeatures.unshift(l);
                    });

                    this._mapboxService.renderData(Layers.Lines, lineFeatures, 'HistoryComponent');
                } else {
                    this._mapboxService.renderData(Layers.Lines, lineFeatures, 'HistoryComponent');
                }

                // Update or remove stops
                if (activity?.activityPoints) {
                    const features = _.map(activity?.activityPoints, (s: ActivityPoint) => s.toFeature());
                    this._mapboxService.renderData(Layers.Stops, features, 'HistoryComponent');
                } else {
                    this._mapboxService.renderData(Layers.Stops, [], 'HistoryComponent');
                }
            });

        // listens to history vehicle position changes
        this._historyStore.getPropertyState('highlightedTime')
            .subscribe((highlightedTime: PassedPosition) => {
                if (highlightedTime) {
                    const vehicleFeature = this._getVehicleFeature(highlightedTime);
                    this._mapboxService.renderData(Layers.Vehicle, [vehicleFeature], 'HistoryComponent');
                } else {
                    this._mapboxService.renderData(Layers.Vehicle, [], 'HistoryComponent');
                }
            });


        // listens to if a stop is selected to clear or draw it in the map
        this._stopStore.getSelectedStopState()
            .subscribe(stops => {
                let features: Feature[] = [];

                if (stops?.length === 1) {
                    const stop = stops[0];
                    if (stop instanceof Stop) {
                        const bounds = new mapboxgl.LngLatBounds();
                        bounds.extend(stop.position);
                        this._mapboxService.fitBounds(bounds, 10);
                        features.push(stop.toFeature(true));
                    } else {
                        features.push(stop.toFeature());
                    }

                } else if (stops?.length > 1) {
                    features = _.map(stops, (s: ActivityPoint | Stop) => s.toFeature());
                } else {
                    // clear map
                    features = [];
                }

                features = _.filter(features, f => f !== undefined);

                // clears map from selected stop
                this._mapboxService.renderData(Layers.Stops, features, 'VehicleComponent');
            });

        this._lineProgressStore.getCurrentFeatures()
            .subscribe(features => {
                this._mapboxService.renderData(Layers.Lines, features, 'VehicleComponent');
            });
    }

    private activateHistoryMode(isHistoryModeActive: boolean) {
        this._isHistoryModeActive = isHistoryModeActive;
        this._mapboxService.setVisibilityOnLayer('traffic',
            !isHistoryModeActive ? this._appStateService.isTrafficLayerVisible : false);
        this._mapboxService.setVisibilityOnLayer(Layers.VehicleWarning, !isHistoryModeActive);
        this._mapboxService.setVisibilityOnLayer(Layers.TimingPoint, isHistoryModeActive);

        if (!isHistoryModeActive) {
            this._mapboxService.renderData(Layers.Lines, [], 'HistoryComponent');
            this._mapboxService.renderData(Layers.Stops, [], 'HistoryComponent');
        }
    }

    private _getPointFeatures(vehicleHistory: PassedPosition[]): Feature[] {
        const pointFeatures: Feature[] = [];

        _.forEach(vehicleHistory, vh => {
            const pointFeature = turf.point([vh.long, vh.lat], { type: 'point', id: vh.id, title: vh.ts.toLocaleTimeString() });
            pointFeatures.push(pointFeature);
        });

        return pointFeatures;
    }

    /**
     * Get a continued line with a single line color, even if vehicle is on or off route
     */
    private _getLineFeatures(vehicleHistory: PassedPosition[]): Feature[] {
        const coordinates = _.map(vehicleHistory, vh => [vh.long, vh.lat]);
        const features: Feature[] = [];

        const properties = {
            zIndex: 10,
            type: 'shape',
            wideLine: true,
            discrete: false
        };

        const linestringFeature = turf.lineString(coordinates, properties);
        features.push(linestringFeature);

        return features;
    }

    /**
     * Get a continued line with discrete color where the vehicle is off route
     */
    private _getLineFeaturesV2(vehicleHistory: PassedPosition[]): Feature[] {
        const features: Feature[] = [];

        let currentSegment: [number, number][] = [];
        let previousDiscreteState = this.shouldBeDiscrete(vehicleHistory[0]);

        for (let i = 0; i < vehicleHistory.length; i++) {
            const vh = vehicleHistory[i];
            const isDiscrete = this.shouldBeDiscrete(vh);

            if (currentSegment.length > 0 && isDiscrete !== previousDiscreteState) {
                // Add the first point of the next segment to maintain connectivity
                currentSegment.push([vh.long, vh.lat]);

                // Create a LineString feature for the previous segment
                const properties = {
                    zIndex: 10,
                    type: 'shape',
                    wideLine: true,
                    discrete: previousDiscreteState
                };

                features.push(turf.lineString(currentSegment, properties));

                // Start a new segment with the last point
                currentSegment = [[vh.long, vh.lat]];
            } else {
                currentSegment.push([vh.long, vh.lat]);
            }

            previousDiscreteState = isDiscrete;
        }

        // Add the last segment
        if (currentSegment.length > 1) {
            const properties = {
                zIndex: 10,
                type: 'shape',
                wideLine: true,
                discrete: previousDiscreteState
            };

            features.push(turf.lineString(currentSegment, properties));
        }

        return features;
    }


    // Example: Define your logic for marking a segment as discrete
    private shouldBeDiscrete(vh: PassedPosition): boolean {
        return vh.isOffRoute;
    }

    private _getPlannedLineFeature(activity: BlockActivityInfo): Feature[] {
        const properties = {
            zIndex: 10,
            type: 'shape',
            discrete: true,
            wideLine: true,
            id: activity.id
        };

        const feature = activity?.toShapeFeature(properties);
        return [feature];
    }

    private _getBounds(vehicleHistory: PassedPosition[]): mapboxgl.LngLatBounds {
        const coordinates = _.map(vehicleHistory, vh => [vh.long, vh.lat]);

        if (coordinates && coordinates.length > 0) {
            if (coordinates[0][0] === undefined) {
                return;
            }

            const firstCoordinate = new mapboxgl.LngLat(coordinates[0][0], coordinates[0][1]);

            const bounds = new mapboxgl.LngLatBounds([firstCoordinate, firstCoordinate]);

            _.each(coordinates, position => {
                if (position[0] !== undefined) {
                    const coordinate = new mapboxgl.LngLat(position[0], position[1]);
                    bounds.extend(coordinate);
                }
            });

            return bounds;
        }

    }

    private _getVehicleFeature(highlightedTime: PassedPosition): Feature {
        return turf.point(
            [highlightedTime.long, highlightedTime.lat],
            {
                type: 'point',
                zIndex: 15,
                icon: highlightedTime.icon,
                hasWarning: false,
                bearing: highlightedTime.bearing,
                title: this._historyVehicleInventoryNumber
            }
        );
    }
}
